import {
    type Department,
    type RosterTab,
    TimeEntryType,
    timeEntryTypeColor,
    type TimeFilter,
    unique,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { css, html, LitElement } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { Checkbox } from "../../../elements/checkbox";
import "../../../elements/entity-multi-select";
import type { EntityMultiSelect } from "../../../elements/entity-multi-select";
import "../../../elements/popover";
import type { Popover } from "../../../elements/popover";
import "../../../elements/sortable-list";
import type { SortableList } from "../../../elements/sortable-list";
import { app, router } from "../../../init";
import { debounce } from "../../../lib/util";
import { StateMixin } from "../../../mixins/state";
import { colors, shared } from "../../../styles";

const defaultTypes = [TimeEntryType.Vacation, TimeEntryType.Sick, TimeEntryType.CompDay, TimeEntryType.Free];

@customElement("ptc-roster-tabs")
export class RosterTabs extends StateMixin(LitElement) {
    @property({ type: Number })
    activeIndex: number;

    private get _activeTab(): RosterTab | undefined {
        const clampedIndex = Math.min(app.rosterTabs.length - 1, Math.max(0, this.activeIndex));
        return app.rosterTabs[clampedIndex];
    }

    @state()
    private _filterString = "";

    @query(".edit-tab-form")
    private _form: HTMLFormElement;

    @query(".form-popover")
    private _formPopover: Popover;

    @query("ptc-sortable-list")
    private _list: SortableList<RosterTab>;

    @query("#filterInput")
    private _filterInput: HTMLInputElement;

    @query("#tabsListPopover")
    private _tabsListPopover: Popover;

    private _updateTab(tab: RosterTab) {
        if (!app.profile) {
            return;
        }
        const form = this._form;
        const departmentsInput = form?.querySelector(
            "ptc-entity-multi-select[name='departments']"
        ) as EntityMultiSelect<Department>;
        const typesInput = form?.querySelector(
            "ptc-entity-multi-select[name='types']"
        ) as EntityMultiSelect<TimeEntryType>;
        if (!form || !departmentsInput || !typesInput) {
            return;
        }
        const data = new FormData(form);
        const name = data.get("name") as string;
        const from = data.get("from") as string;
        const to = data.get("to") as string;
        const departments = departmentsInput.selected.map((d) => d.id);

        // Rebuilt fresh from the select each save (which only ever holds default types), so these
        // Sick-derived types can't accumulate: deselecting Sick simply omits them on the next save.
        const types = [...typesInput.selected];
        if (types.includes(TimeEntryType.Sick)) {
            types.push(TimeEntryType.SickInKUG, TimeEntryType.ChildSick);
        }

        tab.departments = departments.length ? departments : undefined;
        tab.name = name;
        if (!tab.time) {
            tab.time = { from: undefined, to: undefined } as { from?: string; to?: string };
        }
        tab.time.from = from || undefined;
        tab.time.to = to || undefined;
        tab.types = unique(types);
        this._update();
    }

    private async _newTab() {
        if (!app.profile || !this._activeTab) {
            return;
        }

        const newTab: RosterTab = {
            name: "Neuer Reiter",
            venue: this._activeTab.venue,
        };
        app.profile.rosterTabs.push(newTab);
        this._update();
        await this.updateComplete;
        this._selectTab(app.rosterTabs.indexOf(newTab));
        this._tabsListPopover.hide();
        const popover = this._formPopover;
        if (popover) {
            void popover.show(true); // don't await closing of popover
            const nameInput = popover.querySelector("input[name='name']") as HTMLInputElement;
            nameInput && nameInput.select();
        }
    }

    private _removeTab(tab: RosterTab) {
        if (!app.profile || app.rosterTabs.length < 2) {
            return;
        }
        const i = app.profile.rosterTabs.indexOf(tab);
        if (this.activeIndex === i) {
            this._selectTab(Math.max(i - 1, 0));
        }
        app.profile.rosterTabs.splice(i, 1);
        this._update();
        this._formPopover.hide();
    }

    private _update() {
        app.publish();
        this.requestUpdate();
        this._debouncedUpdateProfile();
    }

    private _tabMoved(_tab: RosterTab, i: number) {
        app.profile!.rosterTabs = this._list.items;
        this._update();
        this._selectTab(i);
    }

    private _debouncedUpdateProfile = debounce(() => app.updateRosterTabs(), 3000);

    private _tabIntentTimer?: ReturnType<typeof setTimeout>;

    /** Announces a tab the user is about to pick, so the roster can preload its data. */
    private _scheduleTabIntent(index: number) {
        this._cancelTabIntent();
        this._tabIntentTimer = setTimeout(
            () => this.dispatchEvent(new CustomEvent<{ index: number }>("tab-intent", { detail: { index } })),
            50
        );
    }

    private _cancelTabIntent = () => clearTimeout(this._tabIntentTimer);

    private _selectTab(i: number) {
        const newIndex = Math.min(app.rosterTabs.length - 1, Math.max(0, i));
        if (this.activeIndex !== newIndex) {
            router.go(undefined, { ...router.params, tab: newIndex.toString() });
            this._tabsListPopover.hide();
        }
    }

    private _selectVenue(e: Event, tab: RosterTab) {
        const venue = Number((e.target as HTMLSelectElement).value);
        tab.venue = venue;
        tab.departments = undefined;
        this._update();
    }

    private _cycleFromRule(tab: RosterTab) {
        if (!tab.time) {
            tab.time = {} as TimeFilter;
        }
        const rules = ["closed", "open", "equal"] as const;
        const currRule = tab.time.fromRule || "closed";
        tab.time.fromRule = rules[(rules.indexOf(currRule) + 1) % rules.length];
        this._update();
    }

    private _cycleToRule(tab: RosterTab) {
        if (!tab.time) {
            tab.time = {} as TimeFilter;
        }
        const rules = ["closed", "open", "equal"] as const;
        const currRule = tab.time.toRule || "closed";
        tab.time.toRule = rules[(rules.indexOf(currRule) + 1) % rules.length];
        this._update();
    }

    private _renderTab(tab: RosterTab, i: number) {
        const fs = this._filterString.toLowerCase();
        if (
            fs &&
            (!tab.name.toLowerCase().includes(fs) || !app.getVenue(tab.venue)?.name?.toLowerCase().includes(fs))
        ) {
            return;
        }

        const venue = app.getVenue(tab.venue);

        if (!venue) {
            return;
        }

        const departments = venue.departments.filter((department) => app.hasAccess({ department }));
        const isActive = i === this.activeIndex;

        return html`
            <div
                class="stretch horizontal start-aligning layout click tab ${isActive ? "active" : ""} collapse"
                @click=${() => this._selectTab(i)}
                @pointerenter=${() => !isActive && this._scheduleTabIntent(i)}
                @pointerleave=${this._cancelTabIntent}
            >
                <div class="stretch collapse">
                    <div class="tab-name ellipsis">${tab.name}</div>
                    <div class="horizontal spacing layout">
                        ${app.venues.length > 1
                            ? html`
                                  <div class="pill ellipsis">
                                      <ptc-ui-icon name="icon.object.company"></ptc-ui-icon>
                                      ${venue.name}
                                  </div>
                              `
                            : ""}
                        ${tab.time && (tab.time.from || tab.time.to)
                            ? html`
                                  <div class="black pill">
                                      <ptc-ui-icon name="icon.object.time"></ptc-ui-icon> ${tab.time.from
                                          ? tab.time.from.slice(0, 5)
                                          : "offen"}
                                      - ${tab.time.to ? tab.time.to.slice(0, 5) : "offen"}
                                  </div>
                              `
                            : ""}
                        ${!tab.departments || tab.departments.length === departments.length
                            ? html` <div class="pill">Alle Abteilungen</div> `
                            : tab.departments.length <= 5
                              ? tab.departments.map((id) => {
                                    const { department } = app.getDepartment(id);
                                    return (
                                        department &&
                                        html`
                                            <div
                                                class="pill ellipsis"
                                                style="--color-highlight: ${colors[department.color] ||
                                                department.color}"
                                            >
                                                ${department.name}
                                            </div>
                                        `
                                    );
                                })
                              : html`<div class="pill ellipsis">
                                    <ptc-ui-icon name="icon.action.filter"></ptc-ui-icon> ${tab.departments.length}
                                    Abteilungen
                                </div>`}
                        ${defaultTypes.map((t) =>
                            tab.types && !tab.types.includes(t)
                                ? html`
                                      <div
                                          class="pill strike-through ellipsis"
                                          style="--color-highlight: ${timeEntryTypeColor(t)}"
                                      >
                                          <ptc-ui-icon name="${app.localized.timeEntryTypeIcon(t)}"></ptc-ui-icon>
                                      </div>
                                  `
                                : ""
                        )}
                    </div>
                </div>
                <ptc-ui-icon name="icon.status.success" ?hidden=${!isActive}></ptc-ui-icon>
            </div>
        `;
    }

    private _renderForm() {
        const tab = this._activeTab;
        if (!tab) {
            return;
        }

        const venue = app.getVenue(tab.venue);
        if (!venue) {
            return;
        }

        //custom tabs are created with filters that may be empty, but not undefined
        const isDefault = tab && tab.name === venue.name && !tab.time && !tab.types?.length && !tab.departments?.length;

        const fromRule = (tab.time && tab.time.fromRule) || "closed";
        const toRule = (tab.time && tab.time.toRule) || "closed";
        const departments = venue.departments.filter((department) => app.hasAccess({ department }));

        return html`
            <div class="padded semibold center-aligning horizontal layout border-bottom">
                <div class="stretch half-padded">
                    <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon> Dienstplan-Tab Bearbeiten
                </div>
                <button
                    class="small slim transparent"
                    type="button"
                    @click=${() => this._removeTab(tab)}
                    ?disabled=${app.rosterTabs.length < 2}
                    ?hidden=${isDefault}
                >
                    <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                </button>
            </div>

            <form
                class="double-padded edit-tab-form"
                @change=${() => this._updateTab(tab)}
                autocomplete="off"
                @keydown=${(e: Event) => e.stopPropagation()}
            >
                <div class="field">
                    <label><ptc-ui-icon name="icon.object.form" class="smaller"></ptc-ui-icon> Name</label>
                    <div class="horizontal center-aligning spacing layout">
                        <input
                            name="name"
                            placeholder="Reitername"
                            .value=${tab.name}
                            autocomplete="off"
                            class="stretch"
                        />
                    </div>
                </div>

                ${app.accessibleVenues.length > 1
                    ? html`
                          <div class="field">
                              <label
                                  ><ptc-ui-icon name="icon.object.company" class="smaller"></ptc-ui-icon>
                                  Standort</label
                              >

                              <select @change=${(e: Event) => this._selectVenue(e, tab)} class="venue-selector">
                                  ${app.accessibleVenues.map(
                                      (venue) => html`
                                          <option .value=${venue.id} ?selected=${tab.venue === venue.id}>
                                              ${venue.name}
                                          </option>
                                      `
                                  )}
                              </select>
                          </div>
                      `
                    : ""}

                <div class="field">
                    <label><ptc-ui-icon name="icon.action.filter" class="smaller"></ptc-ui-icon> Abteilungen</label>

                    <ptc-entity-multi-select
                        name="departments"
                        .existing=${departments}
                        .getId=${(dep: Department) => dep.id}
                        .getLabel=${(dep: Department) => dep.name}
                        .getColor=${(dep: Department) => colors[dep.color] || dep.color}
                        .getIcon=${() => "icon.object.department"}
                        .selected=${tab.departments
                            ? tab.departments.map((id) => app.getDepartment(id)!.department)
                            : []}
                        emptyLabel="Alle Abteilungen"
                        emptyIcon="icon.object.department-group"
                        addLabel="Filter Hinzufügen..."
                        noOptionsLabel="Keine Abteilung gefunden."
                        @change=${() => this._updateTab(tab)}
                    ></ptc-entity-multi-select>
                </div>

                <div class="field">
                    <label><ptc-ui-icon name="icon.object.absence-day" class="smaller"></ptc-ui-icon> Fehltage</label>

                    <ptc-entity-multi-select
                        name="types"
                        .existing=${defaultTypes}
                        .getLabel=${(type: TimeEntryType) => app.localized.timeEntryTypeLabel({ type })}
                        .getColor=${(type: TimeEntryType) => timeEntryTypeColor(type)}
                        .getIcon=${(type: TimeEntryType) => app.localized.timeEntryTypeIcon(type)}
                        .selected=${tab.types?.filter((t) => defaultTypes.includes(t)) || [...defaultTypes]}
                        emptyLabel="Keine Fehltage Anzeigen"
                        emptyIcon="icon.toggle.visibility-hidden"
                        addLabel="Hinzufügen..."
                        noOptionsLabel="Nichts gefunden."
                        @change=${() => this._updateTab(tab)}
                    ></ptc-entity-multi-select>
                </div>

                <div class="field">
                    <label><ptc-ui-icon name="icon.object.time" class="smaller"></ptc-ui-icon> Zeitraum</label>

                    <div class="end-aligning horizontal layout" style="overflow: hidden;">
                        <button
                            type="button"
                            class="skinny transparent interval-button ${fromRule === "equal"
                                ? "equal"
                                : fromRule === "open"
                                  ? "open-left"
                                  : "open-right"}"
                            @click=${() => this._cycleFromRule(tab)}
                        ></button>
                        <div class="stretch">
                            <div class="tiny bottom-margined bold text-centering">
                                ${fromRule === "equal"
                                    ? `Beginn genau um`
                                    : fromRule === "open"
                                      ? `Ende nach`
                                      : `Beginn nach einschl.`}
                            </div>
                            <ptc-ui-time-input .value=${tab.time && tab.time.from} name="from"></ptc-ui-time-input>
                        </div>
                        <div class="time-dash">-</div>
                        <div class="stretch">
                            <div class="tiny bottom-margined bold text-centering">
                                ${toRule === "equal"
                                    ? `Ende genau um`
                                    : toRule === "open"
                                      ? `Beginn vor`
                                      : `Ende vor einschl.`}
                            </div>
                            <ptc-ui-time-input .value=${tab.time && tab.time.to} name="to"></ptc-ui-time-input>
                        </div>
                        <button
                            type="button"
                            class="skinny transparent interval-button ${toRule === "equal"
                                ? "equal"
                                : toRule === "open"
                                  ? "open-right"
                                  : "open-left"}"
                            @click=${() => this._cycleToRule(tab)}
                        ></button>
                    </div>
                </div>
            </form>
        `;
    }

    static styles = [
        shared,
        Checkbox.styles,
        css`
            :host {
                display: flex;
                gap: 0.5em;
                overflow-x: auto;
            }

            .tab {
                padding: 0.8em;
                min-width: 15em;
                position: relative;
                overflow: hidden;
                border-bottom: solid 1px var(--shade-1);
            }

            .tab .pill {
                font-size: 0.65em;
                max-width: 10em;
                font-weight: 600;
            }

            .tab:not(:hover) .edit-button {
                opacity: 0;
                position: absolute;
                right: 0;
            }

            .tab.active {
                font-weight: 600;
                color: var(--color-primary);
            }

            .tab-name {
                margin: 0 0.3em 0.5em 0.3em;
            }

            .checkbox-grid {
                display: grid;
                grid-gap: 0.5em;
                grid-template-columns: repeat(auto-fit, minmax(10em, 1fr));
            }

            .divider {
                margin: 0.5em 0;
                font-size: 0.9em;
            }

            ptc-popover {
                z-index: 100;
                width: 30em;
            }

            ptc-ui-time-input {
                width: 100%;
            }

            select {
                width: 100%;
                font-size: 0.9em;
            }

            .interval-button {
                padding: 0 !important;
            }

            .interval-button::after {
                display: block;
                font-size: 1.5em;
                width: 1.5em;
                height: 1.5em;
                line-height: 1.5em;
                text-align: center;
            }

            .interval-button.open-right::after {
                content: "﹝";
                margin: 0 0.1em 0 -0.1em;
            }

            .interval-button.open-left::after {
                content: "﹞";
                margin: 0 -0.1em 0 0.1em;
            }

            .interval-button.equal::after {
                content: "=";
            }

            .time-dash {
                font-size: var(--font-size-large);
                margin: 0.3em 0.6em;
            }

            .filter-indicator {
                position: absolute;
                right: 0.5em;
                bottom: 0.5em;
                width: 1.1em;
                height: 1.1em;
                background: var(--orange);
                border-radius: 1.1em;
                color: var(--color-bg);
            }

            .filter-wrapper ptc-ui-icon[name="icon.action.search"] {
                color: var(--shade-5);
            }

            .filter-wrapper:focus-within ptc-ui-icon[name="icon.action.search"] {
                color: var(--color-primary);
            }
        `,
    ];

    render() {
        const activeTab = this._activeTab;

        return html`
            <button class="slim ghost">
                <div class="horizontal half-spacing center-aligning layout text-left-aligning" style="width: 15em;">
                    <ptc-ui-icon name="icon.planning.roster-tabs"></ptc-ui-icon>
                    <div class="stretch ellipsis collapse">${activeTab?.name}</div>
                    <ptc-ui-icon name="icon.toggle.expand-active"></ptc-ui-icon>
                </div>
            </button>

            <ptc-popover
                style="padding: 0; width: 20em;"
                id="tabsListPopover"
                @popover-hide=${() => (this._filterString = this._filterInput.value = "")}
            >
                <div class="padded semibold border-bottom center-aligning horizontal layout">
                    <div class="stretch half-padded">
                        <ptc-ui-icon name="icon.planning.roster-tabs"></ptc-ui-icon> Dienstplan-Tabs
                    </div>
                    <button class="small slim transparent" @click=${this._newTab}>
                        <ptc-ui-icon name="icon.action.add"></ptc-ui-icon>
                    </button>
                </div>

                <div class="padded filter-wrapper border-bottom" ?hidden=${app.rosterTabs.length < 6}>
                    <ptc-ui-icon name="icon.action.search"></ptc-ui-icon>
                    <input
                        class="plain stretch"
                        id="filterInput"
                        type="text"
                        placeholder="Suchen..."
                        @input=${() => (this._filterString = this._filterInput.value)}
                        @keydown=${(e: Event) => e.stopPropagation()}
                    />
                </div>

                <ptc-scroller style="max-height: 80vh;">
                    <div class="vertical layout">
                        <ptc-sortable-list
                            .items=${app.rosterTabs}
                            .renderItem=${(tab: RosterTab, i: number) => this._renderTab(tab, i)}
                            @item-moved=${(e: CustomEvent<{ item: RosterTab; toIndex: number }>) =>
                                this._tabMoved(e.detail.item, e.detail.toIndex)}
                            ?locked=${!!this._filterString}
                        >
                        </ptc-sortable-list>

                        <div class="padded vertical layout" hidden>
                            <button class="small slim transparent" @click=${this._newTab}>
                                <ptc-ui-icon name="icon.action.add"></ptc-ui-icon>
                            </button>
                        </div>
                    </div>
                </ptc-scroller>
            </ptc-popover>

            <button class="slim transparent relative">
                <ptc-ui-icon name="icon.action.filter"></ptc-ui-icon> ${activeTab?.departments?.length ||
                (activeTab?.types && defaultTypes.some((t) => !activeTab?.types!.includes(t))) ||
                activeTab?.time?.from ||
                activeTab?.time?.to
                    ? html` <div class="tinier filter-indicator">&nbsp;</div> `
                    : ""}
            </button>

            <ptc-popover class="form-popover unpadded">
                <ptc-scroller style="max-height: 80vh;"> ${this._renderForm()} </ptc-scroller>
            </ptc-popover>
        `;
    }
}
